query getSupplyLayerDepthGroups($groupIds: [String]) {
  getGroups(groupIds: $groupIds) {
    id
    type
    hierarchyIds
    isRootForSource
  }
}

fragment TeamAggregationsFields on TeamAggregations {
  memberCount: memberDescCount
  memberFteSum: memberDescFteSum
  vacancyCount: vacancyDescCount
  openRoleCount: openRoleDescCount
  agentCount: agentDescCount
  # Off by default: each of these is a separate DB aggregate, so workspaces
  # without positions must not pay for it. Callers opt in via $withPositions.
  positionCount: positionDescCount @include(if: $withPositions)
  positionFteSum: positionDescFteSum @include(if: $withPositions)
  positionFilledCount: positionFilledDescCount @include(if: $withPositions)
  positionVacantCount: positionVacantDescCount @include(if: $withPositions)
  positionFilledFteSum: positionFilledDescFteSum @include(if: $withPositions)
  positionVacantFteSum: positionVacantDescFteSum @include(if: $withPositions)
  positionDirectReportsMin: positionDirectReportsDescMin
    @include(if: $withPositions)
  positionDirectReportsMax: positionDirectReportsDescMax
    @include(if: $withPositions)
  positionDirectReportsAvg: positionDirectReportsDescAvg
    @include(if: $withPositions)
  positionDirectReportsChart: positionDirectReportsDescChart
    @include(if: $withPositions) {
    points {
      key
      value
    }
  }
  positionManagerCount: positionManagerDescCount @include(if: $withPositions)
  positionUnassignedCount: positionUnassignedDescCount
    @include(if: $withPositions)
  positionExternalManagers: positionExtManagersDesc
    @include(if: $withPositions) {
    personId
    directReportsCount
  }
  positionManagementLayersMin: positionManageLayersDescMin
    @include(if: $withPositions)
  positionManagementLayersMax: positionManageLayersDescMax
    @include(if: $withPositions)
  positionManagementLayersAvg: positionManageLayersDescAvg
    @include(if: $withPositions)
  positionManagementLayersChart: positionManageLayersDescChart
    @include(if: $withPositions) {
    points {
      key
      value
    }
  }
  managerCount: managerDescCount
  unassignedCount: unassignedDescCount
  directReportsMin: directReportsDescMin
  directReportsMax: directReportsDescMax
  directReportsAvg: directReportsDescAvg
  directReportsChart: directReportsDescChart {
    points {
      key
      value
    }
  }
  managementLayersMin: manageLayersDescMin
  managementLayersMax: manageLayersDescMax
  managementLayersAvg: manageLayersDescAvg
  managementLayersChart: manageLayersDescChart {
    points {
      key
      value
    }
  }
  externalManagers: extManagersDesc {
    personId
    directReportsCount
  }
  multiTeamOwnerCount: multiTeamOwnerDescCount
  noDirectOwnerCount: noDirectOwnerDescCount
}

# Parent metrics — Team Insights + Org Chart / useTeamMetrics (do not rename).
query getTeamAggregations(
  $teamId: ID
  $pit: DateTime
  $withPositions: Boolean! = false
) {
  aggregations: getAggregations(pit: $pit) {
    team(teamId: $teamId) {
      ...TeamAggregationsFields
    }
  }
}

# Next-level table atoms only (no charts) — FE supplies direct child ids;
# each id resolves like team() (same pit+id DataLoader keys).
fragment TeamAggregationsNextLevelFields on TeamAggregations {
  memberCount: memberDescCount
  memberFteSum: memberDescFteSum
  positionCount: positionDescCount @include(if: $withPositions)
  positionFteSum: positionDescFteSum @include(if: $withPositions)
  positionFilledCount: positionFilledDescCount @include(if: $withPositions)
  positionVacantCount: positionVacantDescCount @include(if: $withPositions)
  positionFilledFteSum: positionFilledDescFteSum @include(if: $withPositions)
  positionVacantFteSum: positionVacantDescFteSum @include(if: $withPositions)
  positionManagerCount: positionManagerDescCount @include(if: $withPositions)
  positionUnassignedCount: positionUnassignedDescCount
    @include(if: $withPositions)
  positionDirectReportsMin: positionDirectReportsDescMin
    @include(if: $withPositions)
  positionDirectReportsMax: positionDirectReportsDescMax
    @include(if: $withPositions)
  positionDirectReportsAvg: positionDirectReportsDescAvg
    @include(if: $withPositions)
  positionManagementLayersMin: positionManageLayersDescMin
    @include(if: $withPositions)
  positionManagementLayersMax: positionManageLayersDescMax
    @include(if: $withPositions)
  positionManagementLayersAvg: positionManageLayersDescAvg
    @include(if: $withPositions)
  managerCount: managerDescCount
  unassignedCount: unassignedDescCount
  directReportsMin: directReportsDescMin
  directReportsMax: directReportsDescMax
  directReportsAvg: directReportsDescAvg
  managementLayersMin: manageLayersDescMin
  managementLayersMax: manageLayersDescMax
  managementLayersAvg: manageLayersDescAvg
}

query getTeamsAggregations(
  $teamIds: [ID!]!
  $pit: DateTime
  $withPositions: Boolean! = false
) {
  aggregations: getAggregations(pit: $pit) {
    teams(teamIds: $teamIds) {
      teamId
      aggregates {
        ...TeamAggregationsNextLevelFields
      }
    }
  }
}

query getChildTeamsInsights($teamId: String!, $comparisonDate: Date) {
  team: getGroup(groupId: $teamId) {
    id
    name
    type
    memberCount
    childTeams(comparisonDate: $comparisonDate) {
      id
      name
      type
      directParentId
      parentIds
      hierarchy
      hierarchyIds
      isHidden
      isDeleted
      memberCount
      directMemberCount
      directChildTeamCount
      childTeams(comparisonDate: $comparisonDate) {
        id
        name
        type
        isHidden
        isDeleted
        childTeams(comparisonDate: $comparisonDate) {
          id
          name
          type
          isHidden
          isDeleted
          childTeams(comparisonDate: $comparisonDate) {
            id
            name
            type
            isHidden
            isDeleted
          }
        }
      }
      attributes {
        id
        attributeType
        subtype
        label
        value
      }
    }
  }
}
